/**
 * useStableCallback - Create a stable callback that always sees the latest props/state.
 *
 * This creates a callback that:
 * - Has a stable identity (never changes between renders)
 * - Always calls the latest version of the callback
 * - Avoids stale closure issues in event handlers
 *
 * Unlike React 19's useEffectEvent, this can be called from anywhere (event handlers,
 * callbacks, etc.), not just from Effects. Use this when you need a stable callback
 * reference for external libraries that register callbacks.
 *
 * @see https://react.dev/learn/separating-events-from-effects#declaring-an-effect-event
 */

import { useRef } from 'react'

export function useStableCallback<Args extends Array<unknown>, R>(
  callback: (...args: Args) => R,
): (...args: Args) => R {
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  const stableCallbackRef = useRef((...args: Args): R => {
    return callbackRef.current(...args)
  })

  return stableCallbackRef.current
}
